/**
 * Shared avatar contract — the standardized Atlas avatar format.
 *
 * The avatar is the heart of the Atlas Passport: a fixed body template +
 * standardized skeleton + material slots + attachment points. Worlds may
 * override *rendering* but never this identity data, the skeleton, or the
 * attachment topology — that invariance is what makes you the **Recognizable
 * Stranger** across every world.
 *
 * The server mirrors the complexity formula + canonical skeletons in
 * `server/src/lib/avatar.ts` (the SpacetimeDB module can't import across the
 * monorepo). The two MUST stay in sync — the server value is authoritative.
 */

/** The three standardized body templates. */
export type BodyTemplate = 'humanoid' | 'anthro' | 'abstract';

export const BODY_TEMPLATES: readonly BodyTemplate[] = ['humanoid', 'anthro', 'abstract'];

/** One material slot: a tint plus optional (cost-bearing) texture/shader refs. */
export interface MaterialSlot {
  /** Hex color string (e.g. "#c8a07a"). */
  tint: string;
  /** Optional cooked texture reference (adds render cost). */
  textureRef?: string;
  /** Optional shader variant hint (adds render cost). */
  shaderHint?: string;
}

/** slot name -> material. Canonical slots: skin, primary, secondary, accent. */
export type MaterialSlots = Record<string, MaterialSlot>;

/** attach point -> asset ref. Empty in P1 (permission-gated in P2). */
export type Attachments = Record<string, string>;

/** The portable identity appearance — deterministically renders the same everywhere. */
export interface AvatarAppearance {
  bodyTemplate: BodyTemplate;
  /** Standardized skeleton version — derived from the body template, not free-form. */
  skeletonId: string;
  materialSlots: MaterialSlots;
  attachments: Attachments;
}

/** Mirror of the server `avatar` row (appearance + the authoritative complexity). */
export interface Avatar extends AvatarAppearance {
  identity: string;
  /** Server-computed render-cost score. */
  complexity: number;
  updatedAt: bigint;
}

/** Arguments to the `setAvatar` reducer (JSON columns sent as strings). */
export interface SetAvatarArgs {
  bodyTemplate: BodyTemplate;
  skeletonId: string;
  /** JSON-encoded MaterialSlots. */
  materialSlots: string;
  /** JSON-encoded Attachments. */
  attachments: string;
}

// ─── Canonical skeletons ────────────────────────────────────────────────────────

/**
 * The standardized skeleton each body template binds to. Free-form skeleton ids
 * are NOT allowed — `setAvatar` canonicalizes to one of these so the rig topology
 * is invariant and worlds can rely on a fixed attachment layout.
 */
export const CANONICAL_SKELETON: Record<BodyTemplate, string> = {
  humanoid: 'atlas-humanoid-v1',
  anthro: 'atlas-anthro-v1',
  abstract: 'atlas-abstract-v1',
};

export function canonicalSkeleton(body: BodyTemplate): string {
  return CANONICAL_SKELETON[body];
}

// ─── Complexity formula (authoritative on the server; mirrored here) ─────────────

/** Base render-cost by body template. */
export const BODY_BASE_COST: Record<BodyTemplate, number> = {
  humanoid: 100,
  anthro: 150,
  abstract: 60,
};

export const SLOT_COST = 12;
export const TEXTURED_SLOT_COST = 40;
export const SHADER_SLOT_COST = 25;
export const ATTACHMENT_COST = 60;
/** Hard clamp so a malicious payload can't overflow the u32 column. */
export const MAX_COMPLEXITY = 100_000;

/**
 * Compute the Avatar Complexity score (render-cost metric, VRChat-style).
 * Deterministic and identical on both sides. Lower = better compatibility.
 */
export function computeComplexity(a: AvatarAppearance): number {
  let c = BODY_BASE_COST[a.bodyTemplate] ?? BODY_BASE_COST.humanoid;
  for (const slot of Object.values(a.materialSlots)) {
    c += SLOT_COST;
    if (slot.textureRef) c += TEXTURED_SLOT_COST;
    if (slot.shaderHint) c += SHADER_SLOT_COST;
  }
  c += Object.keys(a.attachments).length * ATTACHMENT_COST;
  if (c < 0) c = 0;
  if (c > MAX_COMPLEXITY) c = MAX_COMPLEXITY;
  return c;
}

/** A coarse tier label for the HUD (mirror VRChat's green→red budgeting). */
export function complexityTier(c: number): 'low' | 'medium' | 'high' | 'very-high' {
  if (c <= 150) return 'low';
  if (c <= 350) return 'medium';
  if (c <= 700) return 'high';
  return 'very-high';
}

// ─── Defaults ───────────────────────────────────────────────────────────────────

/** The default appearance seeded at signup (a recognizable humanoid baseline). */
export function defaultAppearance(): AvatarAppearance {
  return {
    bodyTemplate: 'humanoid',
    skeletonId: CANONICAL_SKELETON.humanoid,
    materialSlots: {
      skin: { tint: '#c8a07a' },
      primary: { tint: '#3b6ea5' },
      secondary: { tint: '#2a2f38' },
    },
    attachments: {},
  };
}
